import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { uploadFile, downloadPdf } from "@/lib/api";
import CareerDocCard from "@/components/CareerDocCard";
import { Loader2, Send, FileText, Mail, Briefcase, Upload, Lightbulb, ChevronDown, Download } from "lucide-react";

const CV_TEMPLATE = { url: "/career/cv-template.pdf", filename: "modele_cv_cap_epc.pdf", label: "Modèle de CV vierge", testId: "download-cv-template-btn" };
const CV_SECTIONS = [
  { h: "Structure (1 page max)", items: [
    "En-tête : prénom, nom, téléphone, email, adresse (ville suffit)",
    "Titre clair : « Recherche de PFMP — CAP Equipier Polyvalent du Commerce »",
    "Formation : établissement, classe, année scolaire",
    "Expériences : jobs d'été, stages, babysitting, aide familiale… tout compte",
    "Compétences : mise en rayon, encaissement, accueil client, gestion des stocks",
    "Qualités : ponctualité, sourire, sens du contact, esprit d'équipe",
  ]},
  { h: "Astuces", items: [
    "Une photo n'est pas obligatoire mais peut rassurer le recruteur",
    "Reste honnête : ne mets rien que tu ne pourrais pas expliquer à l'oral",
    "Relis-toi (ou fais-toi relire) : zéro faute d'orthographe",
  ]},
];
const COVER_LETTER_TEMPLATE = { url: "/career/cover-letter-template.pdf", filename: "modele_lettre_motivation_cap_epc.pdf", label: "Modèle de lettre vierge", testId: "download-cover-letter-template-btn" };
const COVER_LETTER_SECTIONS = [
  { h: "Structure", items: [
    "En-tête : tes coordonnées à gauche, celles de l'entreprise à droite, la date",
    "Objet : « Candidature à une PFMP du [date] au [date] »",
    "Formule d'appel : « Madame, Monsieur, »",
    "Paragraphe 1 : qui tu es et ce que tu recherches",
    "Paragraphe 2 : pourquoi CETTE entreprise précisément",
    "Paragraphe 3 : ce que tu peux apporter + tes disponibilités",
    "Formule de politesse + signature",
  ]},
  { h: "Phrase d'accroche possible", items: [
    "« Actuellement en formation CAP Equipier Polyvalent du Commerce, je recherche une entreprise pour effectuer ma période de formation en milieu professionnel du [date] au [date]. »",
  ]},
];

function GuideCard({ icon: Icon, title, sections, accent, template }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const downloadTemplate = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await downloadPdf(template.url, template.filename);
      toast.success(`${template.label} téléchargé ✓`);
    } catch {
      toast.error("Échec du téléchargement.");
    } finally { setBusy(false); }
  };

  return (
    <div className={`rounded-3xl border p-5 sm:p-6 ${accent}`} data-testid={`guide-card-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 text-left">
        <span className="h-10 w-10 rounded-2xl bg-white/70 flex items-center justify-center shrink-0"><Icon className="h-5 w-5" /></span>
        <span className="flex-1">
          <span className="font-display text-lg font-semibold block">{title}</span>
          <span className="text-sm font-medium opacity-70">Astuces pour bien le rédiger</span>
        </span>
        <ChevronDown className={`h-5 w-5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-4 space-y-3">
          {sections.map((s) => (
            <div key={s.h}>
              <p className="text-xs font-extrabold uppercase tracking-wide opacity-70 mb-1">{s.h}</p>
              <ul className="space-y-1">
                {s.items.map((it) => (
                  <li key={it} className="text-sm font-medium leading-relaxed flex gap-2"><span className="opacity-50">•</span><span>{it}</span></li>
                ))}
              </ul>
            </div>
          ))}
          {template && (
            <button
              type="button"
              onClick={downloadTemplate}
              disabled={busy}
              data-testid={template.testId}
              className="inline-flex items-center gap-2 rounded-full bg-white/80 hover:bg-white disabled:opacity-60 px-4 py-2 text-sm font-bold transition-colors"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Télécharger {template.label.toLowerCase()} (PDF)
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function UploadSlot({ type, label, description, doc, icon: Icon, busy, onPick }) {
  const inputRef = useRef(null);
  return (
    <div className="rounded-3xl border border-slate-100 bg-white shadow-sm p-5 sm:p-6">
      <div className="flex items-center gap-3 mb-3">
        <span className="h-10 w-10 rounded-2xl bg-orange-50 text-orange-600 flex items-center justify-center shrink-0"><Icon className="h-5 w-5" /></span>
        <div>
          <p className="font-display text-lg font-semibold text-slate-900">{label}</p>
          <p className="text-sm text-slate-500 font-medium">{description}</p>
        </div>
      </div>
      {doc ? <CareerDocCard doc={doc} /> : <p className="text-sm text-slate-400 font-medium mb-3">Aucun document déposé pour le moment.</p>}
      <input ref={inputRef} type="file" accept=".pdf,.doc,.docx,image/*" className="hidden"
        onChange={(e) => e.target.files[0] && onPick(e.target.files[0])} />
      <button
        type="button"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        data-testid={`upload-${type}-btn`}
        className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white px-4 py-2.5 font-bold text-sm transition-colors"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {doc ? "Remplacer le fichier" : "Déposer mon fichier"}
      </button>
    </div>
  );
}

function PfmpProofSection({ pfmp, proofs, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const addProof = async (file) => {
    setBusy(true);
    try {
      const up = await uploadFile(file);
      await api.post("/career/pfmp-proof", { file_id: up.id, pfmp_id: pfmp.id });
      toast.success("Preuve ajoutée ✓");
      onChanged();
    } catch {
      toast.error("Échec de l'ajout de la preuve.");
    } finally { setBusy(false); }
  };

  const removeProof = async (id) => {
    try {
      await api.delete(`/career/docs/${id}`);
      toast.success("Preuve supprimée");
      onChanged();
    } catch { toast.error("Suppression impossible"); }
  };

  return (
    <div className="rounded-3xl border border-slate-100 bg-white shadow-sm p-5 sm:p-6" data-testid={`pfmp-proof-section-${pfmp.id}`}>
      <div className="flex items-center gap-3 mb-1">
        <span className="h-10 w-10 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0"><Briefcase className="h-5 w-5" /></span>
        <div>
          <p className="font-display text-lg font-semibold text-slate-900">{pfmp.title}</p>
          <p className="text-sm text-slate-500 font-medium">{pfmp.period}</p>
        </div>
      </div>
      <p className="text-xs text-slate-400 font-medium mb-3 mt-2">Mails envoyés, réponses d'entreprise, confirmations… dépose toutes tes preuves de recherche pour cette PFMP.</p>
      <div className="space-y-2.5">
        {proofs.map((p) => <CareerDocCard key={p.id} doc={p} onDelete={removeProof} />)}
        {proofs.length === 0 && <p className="text-sm text-slate-400 font-medium">Aucune preuve déposée pour cette PFMP.</p>}
      </div>
      <input ref={inputRef} type="file" accept=".pdf,.doc,.docx,image/*" className="hidden"
        onChange={(e) => e.target.files[0] && addProof(e.target.files[0])} />
      <button
        type="button"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        data-testid={`add-pfmp-proof-${pfmp.id}`}
        className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-60 px-4 py-2.5 font-bold text-sm transition-colors"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Ajouter une preuve
      </button>
    </div>
  );
}

export default function CareerPage() {
  const [docs, setDocs] = useState(null);
  const [pfmps, setPfmps] = useState([]);
  const [busyType, setBusyType] = useState(null);

  const load = () => {
    api.get("/career/docs").then(({ data }) => setDocs(data)).catch(() => setDocs({ cv: null, cover_letter: null, pfmp_proofs: [] }));
    api.get("/pfmp/list").then(({ data }) => setPfmps(data)).catch(() => setPfmps([]));
  };

  useEffect(() => { load(); }, []);

  const upsertSingle = async (type, file) => {
    setBusyType(type);
    try {
      const up = await uploadFile(file);
      await api.post(type === "cv" ? "/career/cv" : "/career/cover-letter", { file_id: up.id });
      toast.success(type === "cv" ? "CV déposé ✓" : "Lettre de motivation déposée ✓");
      load();
    } catch {
      toast.error("Échec du dépôt du fichier.");
    } finally { setBusyType(null); }
  };

  if (!docs) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 text-orange-500 animate-spin" /></div>;

  return (
    <div className="space-y-8" data-testid="career-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 flex items-center gap-3">
          <Send className="h-8 w-8 text-orange-500" /> Recherche de stage
        </h1>
        <p className="text-slate-500 font-medium mt-1">Prépare ton CV, ta lettre de motivation, et garde une trace de tes démarches pour chaque PFMP.</p>
      </div>

      <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-slate-700 text-white p-5 sm:p-6 flex items-start gap-3">
        <Lightbulb className="h-6 w-6 text-orange-400 shrink-0 mt-0.5" />
        <p className="text-sm font-medium leading-relaxed">Déplie les guides ci-dessous avant de te lancer : un bon CV et une bonne lettre augmentent tes chances de trouver ton entreprise rapidement.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <GuideCard
          icon={FileText}
          title="Comment rédiger mon CV"
          accent="bg-sky-50 border-sky-100 text-sky-900"
          template={CV_TEMPLATE}
          sections={CV_SECTIONS}
        />
        <GuideCard
          icon={Mail}
          title="Comment rédiger ma lettre de motivation"
          accent="bg-orange-50 border-orange-100 text-orange-900"
          template={COVER_LETTER_TEMPLATE}
          sections={COVER_LETTER_SECTIONS}
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <UploadSlot type="cv" label="CV" description="Un seul CV, valable toute l'année. Tu peux le remplacer à tout moment." icon={FileText} doc={docs.cv} busy={busyType === "cv"} onPick={(f) => upsertSingle("cv", f)} />
        <UploadSlot type="lettre" label="Lettre de motivation" description="Une seule lettre, valable toute l'année. Tu peux la remplacer à tout moment." icon={Mail} doc={docs.cover_letter} busy={busyType === "cover_letter"} onPick={(f) => upsertSingle("cover_letter", f)} />
      </div>

      <div>
        <h2 className="font-display text-xl font-semibold text-slate-900 mb-3">Preuves de recherche par PFMP</h2>
        <div className="space-y-4">
          {pfmps.map((p) => (
            <PfmpProofSection key={p.id} pfmp={p} proofs={docs.pfmp_proofs.filter((pr) => pr.pfmp_id === p.id)} onChanged={load} />
          ))}
          {pfmps.length === 0 && <p className="text-sm text-slate-400 font-medium">Aucune PFMP programmée pour ton année.</p>}
        </div>
      </div>
    </div>
  );
}
